<template>
  <div class="pd-page">
    <!-- Loading -->
    <div v-if="loading" class="container py-4">
      <div class="skel skel-cover"></div>
      <div class="skel skel-line w-50 mt-3"></div>
      <div class="skel skel-line w-80 mt-2"></div>
    </div>

    <!-- Error / not found -->
    <div v-else-if="!product" class="pd-state">
      <i class="pi pi-exclamation-triangle"></i>
      <p>لم يتم العثور على هذا المنتج.</p>
      <router-link :to="backLink" class="pd-back-btn">رجوع</router-link>
    </div>

    <template v-else>
      <!-- Cover -->
      <div class="pd-cover" :style="coverStyle">
        <div class="pd-cover__overlay"></div>
        <div class="container pd-cover__content">
          <div class="pd-badges">
            <span class="pd-kind">{{ kind === 'course' ? 'كورس' : 'سنة دراسية' }}</span>
            <span v-if="isOwnYear" class="pd-badge pd-badge--own"><i class="pi pi-star-fill"></i> سنتك الدراسية</span>
            <span class="pd-badge" :class="statusBadge.tone">{{ statusBadge.label }}</span>
          </div>
          <h1 class="pd-title">{{ title }}</h1>
          <p v-if="shortDescription" class="pd-short-desc">{{ shortDescription }}</p>
          <p v-if="product.teacher" class="pd-teacher"><i class="pi pi-user"></i> {{ product.teacher }}</p>
          <span class="pd-price-badge">{{ priceBadgeText }}</span>
        </div>
      </div>

      <div class="container py-4">
        <div class="row g-4">
          <!-- Right (RTL): full description + features + FAQ -->
          <div class="col-lg-7">
            <div class="pd-block">
              <h3 class="pd-h3">عن {{ kind === 'course' ? 'الكورس' : 'السنة الدراسية' }}</h3>
              <p class="pd-desc">{{ product.description || 'لا يوجد وصف متاح.' }}</p>
            </div>

            <div class="pd-block" v-if="stats.length">
              <h3 class="pd-h3">إحصائيات</h3>
              <div class="pd-stats">
                <div v-for="s in stats" :key="s.label" class="pd-stat">
                  <div class="pd-stat__value">{{ s.value }}</div>
                  <div class="pd-stat__label">{{ s.label }}</div>
                </div>
              </div>
            </div>

            <div class="pd-block">
              <h3 class="pd-h3">ماذا ستحصل عليه</h3>
              <ul class="pd-features">
                <li v-for="f in features" :key="f"><i class="pi pi-check-circle"></i> {{ f }}</li>
              </ul>
            </div>

            <div class="pd-block">
              <h3 class="pd-h3">الأسئلة الشائعة</h3>
              <div class="pd-faq">
                <p class="pd-faq__empty"><i class="pi pi-info-circle"></i> لا توجد أسئلة شائعة مضافة بعد لهذا المنتج.</p>
              </div>
            </div>
          </div>

          <!-- Left (RTL): subscription card -->
          <div class="col-lg-5">
            <div class="pd-plans-card">
              <h3 class="pd-h3">{{ isFree ? 'الوصول' : 'الخطط المتاحة' }}</h3>

              <!-- Free product: no subscription required -->
              <template v-if="isFree">
                <div class="pd-free-badge"><i class="pi pi-gift"></i> مجاني</div>
                <button class="pd-subscribe" @click="primaryAction">
                  <i class="pi pi-arrow-left"></i> ابدأ الآن
                </button>
              </template>

              <!-- Priced plans -->
              <template v-else>
                <div v-if="plans.length" class="pd-plans">
                  <button v-for="p in plans" :key="p.type" class="pd-plan"
                          :class="{ active: selectedPlan === p.type }" @click="selectedPlan = p.type">
                    <span class="pd-plan__name">{{ p.label }}</span>
                    <span class="pd-plan__price">{{ money(p.price) }}</span>
                  </button>
                </div>
                <p v-else class="pd-noplans">لا توجد خطط متاحة حالياً.</p>

                <button class="pd-subscribe" :class="{ 'pd-subscribe--access': hasAccess }"
                        :disabled="!hasAccess && (!product.accepts_subscriptions || !plans.length)"
                        @click="primaryAction">
                  <i class="pi" :class="hasAccess ? 'pi-sign-in' : 'pi-bolt'"></i>
                  {{ ctaLabel }}
                </button>
                <p v-if="!hasAccess && !product.accepts_subscriptions" class="pd-hint">الاشتراك غير مفعّل لهذا المنتج حالياً.</p>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { subscriptionService } from '@/services/subscriptionService.js';
import { productPlans, formatMoney } from '@/composables/subscriptionMeta.js';

export default {
  name: 'ProductDetails',
  props: {
    kind: { type: String, default: 'course' },
  },
  data() {
    return { product: null, loading: true, selectedPlan: null };
  },
  computed: {
    id() { return this.$route.params.id; },
    backLink() { return this.kind === 'course' ? '/courses' : '/academic-years'; },
    title() { return this.kind === 'course' ? this.product?.title : this.product?.name; },
    plans() { return productPlans(this.product); },
    coverStyle() {
      const img = this.product?.cover_image_link || this.product?.image_link;
      if (img) return { backgroundImage: `url(${img})` };
      return { background: `linear-gradient(135deg, ${this.product?.color || '#051629'}, #f7b424)` };
    },
    // Hero shows a short excerpt; the full text lives in the "عن..." block below.
    // Course and AcademicYear both only store a single `description` field, so the
    // "short description" is a display-only derivation, not a separate column.
    shortDescription() {
      const text = this.product?.description || '';
      if (!text) return '';
      return text.length > 140 ? text.slice(0, 140).trim() + '…' : text;
    },
    isFree() { return !this.product?.is_subscription_enabled; },
    hasAccess() { return !!this.product?.has_access; },
    isOwnYear() { return this.kind === 'academic_year' && !!this.product?.is_students_year; },
    statusBadge() {
      if (this.hasAccess) return { label: 'أنت مشترك', tone: 'pd-badge--success' };
      if (this.isFree) return { label: 'متاح مجاناً', tone: 'pd-badge--success' };
      if (this.product?.accepts_subscriptions) return { label: 'متاح للاشتراك', tone: 'pd-badge--info' };
      return { label: 'الاشتراك غير متاح حالياً', tone: 'pd-badge--muted' };
    },
    priceBadgeText() {
      if (this.isFree) return 'مجاني';
      const min = this.plans.length ? Math.min(...this.plans.map(p => p.price)) : null;
      return min !== null ? `يبدأ من ${this.money(min)}` : 'السعر غير محدد بعد';
    },
    ctaLabel() {
      if (this.hasAccess) return 'ادخل الآن';
      return 'اشترك الآن';
    },
    stats() {
      // Only surface stats the backend actually provides.
      const s = [];
      if (this.plans.length) s.push({ label: 'خطط متاحة', value: this.plans.length });
      return s;
    },
    features() {
      return this.kind === 'course'
        ? ['وصول كامل لمحتوى الكورس', 'فيديوهات وملفات', 'امتحانات وواجبات', 'بنك أسئلة', 'متابعة مستمرة']
        : ['وصول كامل لمحتوى السنة الدراسية', 'الحصص والوحدات', 'امتحانات وواجبات', 'بنك أسئلة'];
    },
  },
  methods: {
    money: formatMoney,
    async load() {
      this.loading = true;
      try {
        if (this.kind === 'course') {
          this.product = await subscriptionService.course(this.id);
        } else {
          this.product = await subscriptionService.academicYear(this.id);
        }
        if (this.plans.length) this.selectedPlan = this.plans[0].type;
      } catch {
        this.product = null;
      } finally {
        this.loading = false;
      }
    },
    /** Free product, already-subscribed, or fresh subscribe — routes to the right place. */
    primaryAction() {
      if (this.hasAccess || this.isFree) {
        this.$router.push(this.kind === 'academic_year' ? '/contents' : '/my-subscriptions');
        return;
      }
      this.subscribe();
    },
    subscribe() {
      this.$router.push({ path: '/subscribe', query: { type: this.kind, id: this.id, plan: this.selectedPlan || undefined } });
    },
  },
  mounted() { this.load(); },
};
</script>

<style scoped>
.pd-page { min-height: 60vh; padding-bottom: 5rem; }
.pd-cover {
  position: relative; min-height: 240px;
  background-size: cover; background-position: center;
  display: flex; align-items: flex-end;
}
.pd-cover__overlay {
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(15,23,42,.82), rgba(15,23,42,.25));
}
.pd-cover__content { position: relative; padding: 1.5rem 0 1.75rem; color: #fff; }
.pd-badges { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem; margin-bottom: .6rem; }
.pd-kind {
  display: inline-block; background: rgba(255,255,255,.18); backdrop-filter: blur(4px);
  padding: .25rem .75rem; border-radius: 99px; font-size: .75rem; font-weight: 700;
}
.pd-badge {
  display: inline-flex; align-items: center; gap: .3rem;
  padding: .25rem .75rem; border-radius: 99px; font-size: .75rem; font-weight: 700;
  background: rgba(255,255,255,.18); backdrop-filter: blur(4px);
}
.pd-badge--own { background: rgba(245,158,11,.9); color: #1f2937; }
.pd-badge--success { background: rgba(34,197,94,.9); }
.pd-badge--info { background: rgba(5, 22, 41,.85); }
.pd-badge--muted { background: rgba(255,255,255,.14); opacity: .85; }
.pd-title { font-size: clamp(1.5rem, 4vw, 2.2rem); font-weight: 800; margin: 0; }
.pd-short-desc { margin: .5rem 0 0; font-size: .92rem; opacity: .92; max-width: 640px; line-height: 1.7; }
.pd-teacher { margin: .5rem 0 0; font-weight: 700; opacity: .95; }
.pd-price-badge {
  display: inline-block; margin-top: .9rem; background: #fff; color: #4338ca;
  padding: .4rem 1rem; border-radius: 10px; font-weight: 800; font-size: .95rem;
}
.pd-block { margin-bottom: 1.75rem; }
.pd-h3 { font-size: 1.05rem; font-weight: 800; color: #111827; margin-bottom: .75rem; }
.pd-desc { color: #4b5563; line-height: 1.9; font-size: .95rem; }
.pd-stats { display: flex; gap: 1rem; flex-wrap: wrap; }
.pd-stat { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 14px; padding: 1rem 1.4rem; text-align: center; }
.pd-stat__value { font-size: 1.5rem; font-weight: 800; color: #051629; }
.pd-stat__label { font-size: .8rem; color: #6B7280; }
.pd-features { list-style: none; padding: 0; margin: 0; display: grid; gap: .6rem; }
.pd-features li { display: flex; align-items: center; gap: .5rem; color: #374151; font-size: .92rem; }
.pd-features .pi-check-circle { color: #22c55e; }
.pd-plans-card {
  background: #fff; border: 1px solid rgba(5, 22, 41,.12); border-radius: 20px;
  padding: 1.4rem; position: sticky; top: 1rem;
  box-shadow: 0 8px 24px rgba(5, 22, 41,.08);
}
.pd-plans { display: grid; gap: .6rem; margin-bottom: 1rem; }
.pd-plan {
  display: flex; justify-content: space-between; align-items: center;
  border: 1.5px solid #E5E7EB; background: #fff; border-radius: 12px;
  padding: .8rem 1rem; cursor: pointer; transition: all .15s ease;
}
.pd-plan.active { border-color: #051629; background: rgba(5, 22, 41,.05); }
.pd-plan__name { font-weight: 700; color: #374151; }
.pd-plan__price { font-weight: 800; color: #051629; }
.pd-noplans { color: #94a3b8; font-size: .88rem; }
.pd-free-badge {
  display: flex; align-items: center; justify-content: center; gap: .5rem;
  background: rgba(34,197,94,.1); color: #16a34a; border: 1.5px solid rgba(34,197,94,.3);
  border-radius: 12px; padding: .9rem; font-weight: 800; font-size: 1.05rem; margin-bottom: 1rem;
}
.pd-subscribe {
  width: 100%; background: linear-gradient(90deg, #051629, #f7b424); color: #fff;
  border: none; border-radius: 12px; padding: .8rem; font-weight: 800; font-size: 1rem; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: .5rem;
}
.pd-subscribe--access { background: linear-gradient(90deg, #22c55e, #16a34a); }
.pd-subscribe:disabled { background: #cbd5e1; cursor: not-allowed; }
.pd-hint { font-size: .78rem; color: #94a3b8; text-align: center; margin: .6rem 0 0; }
.pd-faq__empty {
  color: #94a3b8; font-size: .88rem; background: #f8fafc; border: 1px dashed #E5E7EB;
  border-radius: 12px; padding: .9rem 1rem; display: flex; align-items: center; gap: .5rem; margin: 0;
}
.pd-state { text-align: center; padding: 4rem 1rem; color: #94a3b8; }
.pd-state .pi { font-size: 2.6rem; display: block; margin-bottom: .8rem; }
.pd-back-btn { display: inline-block; margin-top: .5rem; background: #051629; color: #fff; border-radius: 10px; padding: .5rem 1.2rem; font-weight: 700; text-decoration: none; }
.skel { background: #e9edf5; border-radius: 10px; position: relative; overflow: hidden; }
.skel::after { content: ''; position: absolute; inset: 0; transform: translateX(-100%); background: linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent); animation: sh 1.3s infinite; }
@keyframes sh { 100% { transform: translateX(100%); } }
.skel-cover { height: 200px; } .skel-line { height: 14px; } .w-50 { width: 50%; } .w-80 { width: 80%; }
</style>
